/**
 * Which copies of a model are recent enough to be worth offering, computed on the page.
 *
 * A live model cannot be edited, so every change asked for on one ends in the same
 * offer: copy it and work there. Asked twice in one sitting the agent made two copies
 * and wrote the change to neither, because nothing it could read said the first one
 * existed.
 *
 * The agent has a lookup for this, and it sits inside the write it never attempts: on a
 * live model it answers the request from what it already has rather than calling a tool
 * that can only refuse. So the copies were found on a path nobody reached, and the first
 * reply, the one that actually makes the offer, was written blind. This is the other end
 * of that: the whole model list is already server-rendered onto every model page
 * (`renderView.tsx` -> `store.tenantModels`), so the answer is here for nothing, and the
 * chat sends it with the turn.
 *
 * This is a PORT of rgip's `app/sources/springbok/modelcopies.py` (`recent_copies`), and
 * the two must agree: the same rows, in the same order. It is deliberately narrow for
 * the same reason as the original. Not "does this model have copies", because a tenant
 * accumulates abandoned drafts for years and "you already have a copy" is a useless
 * sentence about one from March. Only copies new enough to plausibly be the work in
 * front of the user right now.
 *
 * It decides nothing. Making another copy stays available whatever this returns, because
 * Studio's own Duplicate button has no limit and an assistant that refuses what the
 * button beside it allows is unusable rather than safe.
 */

/** How recent a copy has to be to be worth naming. */
export const WINDOW_DAYS = 7;

/**
 * A model row as the page holds one.
 *
 * Structural, not `ModelItem`. The store is JSON-serialised into the page and assigned
 * straight back on the client (`store/index.ts`: `this.tenantModels = state.tenantModels`),
 * so what this walks is plain objects that never went through the constructor. Reading
 * them by shape is what they are, and anything relying on the class would work on the
 * server and be undefined in the browser.
 */
export interface ModelRow {
  modelId?: number | string;
  name?: string;
  /**
   * **camelCase, and formatted UTC.** `listModels` builds every row with
   * `utc(model.created_at).format('YYYY-MM-DD HH:mm:ss')` and assigns it to `createdAt`
   * (`src/back/tenantmodels/index.ts`), so the snake_case spelling the service sends is
   * gone by the time it reaches this page. Read as `created_at` this is undefined on
   * every row, every copy fails the age test, and the list comes back empty for every
   * model there has ever been, which looks exactly like a tenant with no recent copies.
   * That is not hypothetical: it shipped once.
   */
  createdAt?: string;
  live?: boolean;
  is_archived?: boolean;
  duplicateOf?: number | string;
}

/** A copy worth offering, with the timestamp still on it. */
export interface RecentCopy {
  model_id: string;
  name: string;
  created_at: string;
}

/** A copy as the agent receives it: an id to send them to, a name to call it, an age. */
export interface ModelCopyContext {
  model_id: string;
  name: string;
  age: string;
}

/**
 * `YYYY-MM-DD HH:mm:ss` as UTC milliseconds, or null when it is not a stamp.
 *
 * Parsed by hand rather than with `Date.parse`, which reads a space-separated stamp with
 * no zone as LOCAL time. Every row here is UTC (`listModels` formats it through
 * `moment.utc`), so `Date.parse` is wrong by the reader's own offset: an hour-old copy
 * reads as three hours old in Berlin, and one made in the last hour reads as being from
 * the future in New York.
 *
 * Unparseable means "cannot prove this is recent", which excludes the row. The whole
 * value of this list is that everything on it is current, and a row of unknown age
 * quietly on it is the abandoned-March-draft case coming back in.
 */
function parseUtcStamp(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(
    value.trim()
  );
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const at = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s)
  );
  return Number.isNaN(at) ? null : at;
}

/**
 * Copies of `sourceModelId` made in the last `withinDays`, newest first.
 *
 * Excluded, each for its own reason: archived ones, which somebody has already put away;
 * live ones, because a live copy cannot be edited either, so offering one would walk the
 * user into the refusal they are already reading; and anything whose creation time will
 * not parse.
 *
 * Ids are compared as strings. The store holds `modelId` and `duplicateOf` as numbers
 * while the id in hand comes off the route as a string, and `13 !== '13'` would make this
 * return nothing at all for every model: an empty list is indistinguishable from "no
 * recent copies", so the failure would look exactly like the thing working.
 */
export function recentCopies(
  models: ReadonlyArray<ModelRow> | undefined | null,
  sourceModelId: string | number | undefined | null,
  now: number = Date.now(),
  withinDays: number = WINDOW_DAYS
): RecentCopy[] {
  if (!Array.isArray(models)) return [];
  const wanted = String(sourceModelId ?? '');
  if (!wanted) return [];
  const cutoff = now - withinDays * 24 * 60 * 60 * 1000;
  return models
    .filter((row) => row && typeof row === 'object')
    .filter((row) => String(row.duplicateOf ?? '') === wanted)
    .filter((row) => !row.is_archived && !row.live)
    .map((row) => ({ row, at: parseUtcStamp(row.createdAt) }))
    .filter(
      (entry): entry is { row: ModelRow; at: number } =>
        entry.at !== null && entry.at >= cutoff
    )
    .sort((a, b) => b.at - a.at)
    .map(({ row }) => ({
      model_id: String(row.modelId),
      name: String(row.name ?? ''),
      created_at: String(row.createdAt),
    }));
}

/**
 * How long ago, in the reader's words.
 *
 * Sent as a phrase rather than as the timestamp so the agent never does the arithmetic:
 * a date in the context is a date it has to subtract today from, in a turn where it is
 * already being asked to keep a list straight, and it gets that wrong for the same
 * reason it gets every other calculation wrong. The window caps this at a week, so there
 * is no branch here for anything older.
 */
export function ageOf(createdAt: string, now: number = Date.now()): string {
  const at = parseUtcStamp(createdAt);
  if (at === null) return '';
  const mins = Math.round((now - at) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/**
 * The copies as the turn sends them: id, name, age, and nothing else.
 *
 * A few dozen tokens for the whole list. The agent needs an id to send somebody to, a
 * name to call it by, and enough of when to tell one from another; every other column on
 * a model row is something it would have to be told to ignore.
 *
 * Aged HERE rather than where the list is filtered, because the panel outlives the page
 * load by however long the session lasts, and "5 min ago" pinned at render time is wrong
 * by the whole of it.
 */
export function copiesForContext(
  copies: ReadonlyArray<RecentCopy>,
  now: number = Date.now()
): ModelCopyContext[] {
  return copies.map((copy) => ({
    model_id: copy.model_id,
    name: copy.name,
    age: ageOf(copy.created_at, now),
  }));
}
